import { normalizeSizeLabel, parseSizeList } from "@/lib/manufacturing/groupMembers";
import type { MfgRecipeKind } from "@/lib/manufacturing/types";

export type SeriesSizeProductPlan = {
  code: string;
  name: string;
  kind: Extract<MfgRecipeKind, "product">;
  tags: string[];
  recipe_group_id: string;
  sizeLabel: string;
  /** Size shells start incomplete until a real BOM is authored. */
  needs_review: true;
};

export type SeriesWizardGapBrief = {
  label: string;
  brief_text: string;
};

/** Derive a series code prefix from a display name (e.g. "Classic" → "CLASSIC"). */
export function seriesCodeFromName(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 10);
}

/** Normalize an explicit series code prefix for product codes. */
export function normalizeSeriesCode(code: string): string {
  return code.trim().toUpperCase().replace(/\s+/g, "-").replace(/^-+|-+$/g, "");
}

/**
 * Resolve the series name + code used for per-size product identity.
 * Prefer explicit series fields, then new-group fields, then the ensured group,
 * then a code derived from the resolved name. Never invent "SERIES" when a name exists.
 */
export function resolveSeriesIdentity(input: {
  seriesName: string;
  seriesCode: string;
  newGroupName?: string;
  newGroupCode?: string;
  groupName?: string;
  groupCode?: string;
}): { ok: true; seriesName: string; seriesCode: string } | { ok: false; error: string } {
  const seriesName =
    input.seriesName.trim() ||
    (input.newGroupName ?? "").trim() ||
    (input.groupName ?? "").trim();
  if (!seriesName) return { ok: false, error: "Series name is required" };

  const seriesCode =
    normalizeSeriesCode(input.seriesCode) ||
    normalizeSeriesCode(input.newGroupCode ?? "") ||
    normalizeSeriesCode(input.groupCode ?? "") ||
    seriesCodeFromName(seriesName);
  if (!seriesCode) return { ok: false, error: "Series code is required" };

  return { ok: true, seriesName, seriesCode };
}

/** Build per-size product create payloads for a series/group. */
export function buildSeriesSizeProducts(input: {
  seriesName: string;
  seriesCode: string;
  sizesRaw: string;
  recipe_group_id: string;
}): { ok: true; products: SeriesSizeProductPlan[] } | { ok: false; error: string } {
  const identity = resolveSeriesIdentity({
    seriesName: input.seriesName,
    seriesCode: input.seriesCode,
  });
  if (!identity.ok) return identity;
  if (!input.recipe_group_id.trim()) return { ok: false, error: "Recipe group is required" };

  const sizes = parseSizeList(input.sizesRaw);
  if (sizes.length === 0) {
    return { ok: false, error: "Add at least one size (e.g. 8x10, 10x12, 12x16)" };
  }

  const { seriesName, seriesCode } = identity;
  const products: SeriesSizeProductPlan[] = sizes.map((sizeLabel) => ({
    code: `${seriesCode}-${sizeLabel}`,
    name: `${seriesName} ${sizeLabel}`,
    kind: "product",
    tags: [`size:${sizeLabel}`],
    recipe_group_id: input.recipe_group_id.trim(),
    sizeLabel,
    needs_review: true,
  }));

  return { ok: true, products };
}

/** Parse gap lines: `label | brief` or just brief text (label = first 48 chars). */
export function parseSeriesGapBriefs(raw: string): SeriesWizardGapBrief[] {
  const lines = raw
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  const out: SeriesWizardGapBrief[] = [];
  for (const line of lines) {
    const pipe = line.indexOf("|");
    if (pipe >= 0) {
      const label = line.slice(0, pipe).trim();
      const brief_text = line.slice(pipe + 1).trim();
      if (!brief_text) continue;
      out.push({ label: label || brief_text.slice(0, 48), brief_text });
      continue;
    }
    out.push({ label: line.slice(0, 48), brief_text: line });
  }
  return out;
}

export function sizeLabelOrNull(raw: string): string | null {
  return normalizeSizeLabel(raw);
}
